import { CurrentTimeLine } from "@v2/components/calendar/CalendarDayView/CurrentTimeLine";
import { ScrollArea, ScrollAreaProps } from "@v2/components/ui/scroll-area";
import { cn } from "@v2/lib/utils";
import { useOptionalReserveForm, } from "@v2/reserve/ReverseFormContext";
import { addMinutes, differenceInMinutes, format, subMinutes } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import React from "react";
import { CalendarEvent, CalendarEventPropsExpanded } from "./CalendarEvent";
import { HourMarker } from "./HourMarker";
import { getHourMarkers, getOverlappingGroups, isToday } from "./helpers";
import { CalendarEvent as OriginalCalendarEvent } from "./types";

import type { ISchedule, ISlot, Office } from "@gettactic/api";
import { getScheduleDateWithTime } from "@gettactic/helpers";
import { getReservationDataFromSlot } from "@gettactic/helpers/src/reservation/getReservationDataFromSlot";
import { useResourceEvents } from "@gettactic/hooks";

export interface ICalendarEvent extends OriginalCalendarEvent {
  isDraggable?: boolean;
  isResizable?: boolean;
}

export interface CalendarDayViewProps {
  events: ICalendarEvent[];
  currentDate?: Date;
  onDateChange?: (date: Date) => void;
  className?: string;
  scrollAreaProps?: ScrollAreaProps;
  office?: Office;
  editModalData?: {
    editModal: boolean;
    schedule: ISchedule;
    canRespondCancel?: boolean;
  } | null;
  setEditModalData?: (
    data: {
      editModal: boolean;
      schedule: ISchedule;
      canRespondCancel?: boolean;
    } | null
  ) => void;
  cancelModalData?: { open: boolean; schedules: ISchedule[] } | null;
  setCancelModalData?: (
    data: { open: boolean; schedules: ISchedule[] } | null
  ) => void;
  isReserving?: boolean;
}

const variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0
  }),
  center: { zIndex: 1, x: 0, opacity: 1 },
  exit: (direction: number) => ({
    zIndex: 0,
    x: direction < 0 ? 1000 : -1000,
    opacity: 0
  })
};

const PIXELS_PER_MINUTE = 1;
const SNAP_MINUTES = 5;
const MIN_DURATION_MIN = 5;

function clampToDay(date: Date, baseDay: Date) {
  const startOfDay = new Date(baseDay);
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date(baseDay);
  endOfDay.setHours(23, 59, 59, 999);
  if (date < startOfDay) return startOfDay;
  if (date > endOfDay) return endOfDay;
  return date;
}
function roundToSnap(date: Date, snapMinutes: number) {
  const ms = date.getTime();
  const snapMs = snapMinutes * 60 * 1000;
  return new Date(Math.round(ms / snapMs) * snapMs);
}

export const DayViewCalendar: React.FC<CalendarDayViewProps> = ({
  currentDate = new Date(),
  onDateChange,
  events,
  className,
  scrollAreaProps,
  office,
  editModalData,
  setEditModalData,
  cancelModalData,
  setCancelModalData,
  isReserving
}) => {
  const reserve = useOptionalReserveForm();
  const durationMinutes: number =
    reserve?.form?.watch("duration")?.value ?? reserve?.durationMins ?? 30;
  const startTime: Date | undefined =
    reserve?.form?.watch("time") ?? reserve?.startTime ?? undefined;
  const formDate = reserve?.form?.watch("weekDates")?.[0] ?? startTime;
  const reserveTitle = reserve?.form?.watch("title");
  const [filteredEvents, setFilteredEvents] = React.useState<ICalendarEvent[]>(
    []
  );
  const [direction, setDirection] = React.useState(0);

  const targetTz = React.useMemo(
    () => office?.time_zone ?? "UTC",
    [office?.time_zone]
  );

  const [prevDate, setPrevDate] = React.useState(currentDate);

  const calendarRef = React.useRef<HTMLDivElement>(null);
  const eventsContainerRef = React.useRef<HTMLDivElement>(null);
  const hourMarkers = getHourMarkers();

  const [externallyDraggingEventId, setExternallyDraggingEventId] =
    React.useState<string | null>(null);

  const [bgDragging, setBgDragging] = React.useState(false);
  const bgDragRef = React.useRef<{
    pointerId: number | null;
    startY: number;
    startDate: Date;
    targetEventId: string | null;
    originalDurationMin: number;
  } | null>(null);

  const { data: resourceSchedules } = useResourceEvents({
    time: currentDate.toISOString(),
    officeId: office?.id ?? null,
    tz: targetTz,
    resourceId: (isReserving && reserve?.resource?.id) || null,
    accountForTZ: false,
  });

  React.useEffect(() => {
    // Use date-only (year-month-day) without additional timezone conversion
    const currentDateStr = format(currentDate, "yyyy-MM-dd");

    if (prevDate && currentDate) {
      const timeDiff = currentDate.getTime() - prevDate.getTime();
      setDirection(timeDiff > 0 ? 1 : -1);
    }

    const next = events.filter((event) => {
      const eventDate = format(new Date(event.startTime), "yyyy-MM-dd");
      return eventDate === currentDateStr;
    }) as (CalendarEventPropsExpanded & {
      isDraggable?: boolean;
      isResizable?: boolean;
    })[];

    if (resourceSchedules?.length) {
      resourceSchedules.forEach((schedule: ISchedule[] | ISchedule) => {
        const scheduleData = (schedule as ISchedule[])
          ? (schedule as ISchedule[])?.[0]
          : (schedule as ISchedule);
        if (!scheduleData) return;

        const { allowCheckIn, allowCheckOut, canEdit, canRespondCancel } =
          getReservationDataFromSlot(
            scheduleData?.slots?.[0] as unknown as ISlot
          );

        next.push({
          id: `reserved-${scheduleData?.id}`,
          title: scheduleData?.title ?? "Reserved",
          startTime: getScheduleDateWithTime(scheduleData?.start),
          endTime: getScheduleDateWithTime(scheduleData?.end),
          location: "",
          color: "rgba(150,150,150,0.2)",
          category: "",
          allowCheckIn: allowCheckIn ?? false,
          allowCheckOut: allowCheckOut ?? false,
          // isResizable: false,
          // isDraggable: false,

          // for now dots are always disabled
          canEdit: false,
          canRespondCancel: false
          // canEdit: canEdit ?? false,
          // canRespondCancel: canRespondCancel ?? false
        });
      });
    }

    if (startTime && isReserving) {
      // Check if the draggable event belongs to the current day (date-only)
      const startTimeDateStr = format(startTime, "yyyy-MM-dd");

      if (startTimeDateStr === currentDateStr) {
        const t = startTime;

        // Get buffer settings from context (reactive) or from ref (fallback)
        const contextBuffer = reserve?.currentBufferSettings;
        const refBuffer = reserve?.wpRequestsRef?.current?.getBufferSettings?.();
        const bufferBefore = contextBuffer?.bufferBefore ?? refBuffer?.bufferBefore ?? 0;
        const bufferAfter = contextBuffer?.bufferAfter ?? refBuffer?.bufferAfter ?? 0;

        // Add buffer before block if there's buffer time
        if (bufferBefore > 0) {
          next.push({
            id: "buffer-before",
            title: "Buffer",
            startTime: subMinutes(t, bufferBefore) as never as string,
            endTime: t as never as string,
            location: "",
            color: "rgba(255,165,0,0.4)", // Orange for buffer
            category: "",
            allowCheckIn: false,
            allowCheckOut: false,
            canEdit: false,
            canRespondCancel: false,
            isDraggable: false,
            isResizable: false,
            isBufferBlock: true
          });
        }

        // Add main reservation event
        next.push({
          id: "asd",
          title: reserveTitle || "Title",
          startTime: t as never as string,
          endTime: addMinutes(t, durationMinutes) as never as string,
          location: "",
          color: "rgba(25,141,202,0.6)",
          category: "",
          allowCheckIn: false,
          allowCheckOut: false,
          canEdit: false,
          canRespondCancel: false,
          isDraggable: true,
          isResizable: true,
          bufferBefore,
          bufferAfter
        });

        // Add buffer after block if there's buffer time
        if (bufferAfter > 0) {
          const endTime = addMinutes(t, durationMinutes);
          next.push({
            id: "buffer-after",
            title: "Buffer",
            startTime: endTime as never as string,
            endTime: addMinutes(endTime, bufferAfter) as never as string,
            location: "",
            color: "rgba(255,165,0,0.4)", // Orange for buffer
            category: "",
            allowCheckIn: false,
            allowCheckOut: false,
            canEdit: false,
            canRespondCancel: false,
            isDraggable: false,
            isResizable: false,
            isBufferBlock: true
          });
        }
      }
    }

    setFilteredEvents(next);
    setPrevDate(currentDate);
  }, [
    currentDate,
    events,
    prevDate,
    reserve?.resourceType,
    reserve?.wpRequestsRef,
    reserve?.currentBufferSettings,
    startTime,
    durationMinutes,
    reserveTitle,
    resourceSchedules,
    formDate,
    targetTz,
    isReserving
  ]);
  React.useEffect(() => {
    if (calendarRef.current && isToday(currentDate)) {
      const now = new Date();
      const currentHour = now.getHours();
      calendarRef.current.scrollTop = Math.max(0, (currentHour - 1) * 60);
    } else if (calendarRef.current) {
      calendarRef.current.scrollTop = 8 * 60;
    }
  }, [currentDate]);

  const updateEventLocal = React.useCallback(
    (id: string, start: Date, end: Date) => {
      setFilteredEvents((prev) =>
        prev.map((e: any) =>
          e.id === id ? { ...e, startTime: start, endTime: end } : e
        )
      );
    },
    []
  );

  const yToDate = React.useCallback(
    (y: number) => {
      const minutesFromStart = y / PIXELS_PER_MINUTE;
      const d = new Date(currentDate);
      d.setHours(0, 0, 0, 0);
      d.setMinutes(d.getMinutes() + minutesFromStart);
      return d;
    },
    [currentDate]
  );

  const pickControllableEventId = React.useCallback((): {
    id: string | null;
    durationMin: number;
  } => {
    const reserveCandidate = filteredEvents.find(
      (e) => e.id === "asd" && (e.isDraggable || e.isResizable)
    );
    if (reserveCandidate) {
      const dur = Math.max(
        MIN_DURATION_MIN,
        differenceInMinutes(
          new Date(reserveCandidate.endTime),
          new Date(reserveCandidate.startTime)
        )
      );
      return { id: reserveCandidate.id, durationMin: dur };
    }
    const anyDraggable = filteredEvents.find(
      (e) => e.isDraggable || e.isResizable
    );
    if (anyDraggable) {
      const dur = Math.max(
        MIN_DURATION_MIN,
        differenceInMinutes(
          new Date(anyDraggable.endTime),
          new Date(anyDraggable.startTime)
        )
      );
      return { id: anyDraggable.id, durationMin: dur };
    }
    return { id: null, durationMin: MIN_DURATION_MIN };
  }, [filteredEvents]);

  const onBgPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest?.('[data-calendar-event="true"]')) return;
    if (target.closest?.('[data-resize-handle="true"]')) return;

    // Ignore clicks on buttons and interactive elements
    if (target.tagName === "BUTTON" || target.closest?.("button")) return;
    if (target.tagName === "SVG" || target.closest?.("svg")) return;

    const container = eventsContainerRef.current;
    if (!container) return;

    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);

    const rect = container.getBoundingClientRect();
    const y = e.clientY - rect.top + container.scrollTop;

    const startDateRaw = yToDate(y);
    const start = clampToDay(
      roundToSnap(startDateRaw, SNAP_MINUTES),
      currentDate
    );

    const { id: targetEventId, durationMin } = pickControllableEventId();
    if (!targetEventId) return;

    bgDragRef.current = {
      pointerId: e.pointerId,
      startY: y,
      startDate: start,
      targetEventId,
      originalDurationMin: durationMin
    };
    setExternallyDraggingEventId(targetEventId);
    setBgDragging(true);

    const end = clampToDay(
      roundToSnap(addMinutes(start, durationMin), SNAP_MINUTES),
      currentDate
    );
    updateEventLocal(targetEventId, start, end);
    e.preventDefault();
  };

  const onBgPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!bgDragRef.current) return;
    const container = eventsContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const yNow = e.clientY - rect.top + container.scrollTop;

    let s = bgDragRef.current.startDate;
    let eDate = yToDate(yNow);

    if (eDate < s) [s, eDate] = [eDate, s];

    s = clampToDay(roundToSnap(s, SNAP_MINUTES), currentDate);
    eDate = clampToDay(roundToSnap(eDate, SNAP_MINUTES), currentDate);

    if (differenceInMinutes(eDate, s) < MIN_DURATION_MIN) {
      eDate = addMinutes(s, MIN_DURATION_MIN);
    }

    updateEventLocal(bgDragRef.current.targetEventId!, s, eDate);
    e.preventDefault();
  };

  const onBgPointerEnd = () => {
    if (!bgDragRef.current) return;
    const { targetEventId } = bgDragRef.current;

    const ev = filteredEvents.find((e) => e.id === targetEventId);
    if (ev) {
      const s = clampToDay(
        roundToSnap(new Date(ev.startTime), SNAP_MINUTES),
        currentDate
      );
      const eDate = clampToDay(
        roundToSnap(new Date(ev.endTime), SNAP_MINUTES),
        currentDate
      );
      reserve?.form?.setValue?.("time", s);

      reserve?.setStartTime?.(s);

      setTimeout(() => {
        const mins = Math.max(MIN_DURATION_MIN, differenceInMinutes(eDate, s));
        reserve?.form?.setValue?.("duration", { value: mins });
        reserve?.setDurationMins?.(mins);
      }, 1);
    }

    bgDragRef.current = null;
    setExternallyDraggingEventId(null);
    setBgDragging(false);
  };

  // const handleEditModal = (scheduleId: ISchedule['id']) => {
  //   const schedule = filteredEvents.find((e) => e.id === scheduleId)?.schedule as ISchedule;
  //   if (!schedule) return;
  //   setEditModalData?.({
  //     editModal: true,
  //     schedule,
  //     canRespondCancel: true
  //   });
  // };

  // const handleCancelModal = (scheduleId: ISchedule['id']) => {
  //   const schedule = filteredEvents.find((e) => e.id === scheduleId)?.schedule as ISchedule;
  //   if (!schedule) return;
  //   setCancelModalData?.({
  //     open: true,
  //     schedules: schedule ? [schedule] : []
  //   });
  // };

  const renderEvents = () => {
    const groups = getOverlappingGroups(filteredEvents);
    return groups.flatMap((group) => {
      const width = 100 / group.length;
      return group.map((event, eventIndex) => (
        <CalendarEvent
          // handleEditModal={handleEditModal}
          // handleCancelModal={handleCancelModal}
          key={event.id}
          event={event}
          width={width - 1}
          left={eventIndex * width}
          isDraggable={!!event.isDraggable}
          isResizable={!!event.isResizable}
          externalInteracting={externallyDraggingEventId === event.id}
          containerRef={eventsContainerRef}
          yToDate={yToDate}
          onMove={(id, newStart, newEnd) => {
            const s = clampToDay(
              roundToSnap(newStart, SNAP_MINUTES),
              currentDate
            );
            const e = clampToDay(
              roundToSnap(newEnd, SNAP_MINUTES),
              currentDate
            );
            const safeEnd =
              differenceInMinutes(e, s) < MIN_DURATION_MIN
                ? addMinutes(s, MIN_DURATION_MIN)
                : e;
            updateEventLocal(id, s, safeEnd);
          }}
          onResize={(id, edge, newStart, newEnd) => {
            const s = clampToDay(
              roundToSnap(newStart, SNAP_MINUTES),
              currentDate
            );
            const e = clampToDay(
              roundToSnap(newEnd, SNAP_MINUTES),
              currentDate
            );
            if (differenceInMinutes(e, s) < MIN_DURATION_MIN) {
              if (edge === "start") {
                const safeStart = addMinutes(e, -MIN_DURATION_MIN);
                updateEventLocal(id, clampToDay(safeStart, currentDate), e);
              } else {
                const safeEnd = addMinutes(s, MIN_DURATION_MIN);
                updateEventLocal(id, s, clampToDay(safeEnd, currentDate));
              }
            } else {
              updateEventLocal(id, s, e);
            }
          }}
          onCommit={(id, finalStart, finalEnd) => {
            const s = clampToDay(
              roundToSnap(finalStart, SNAP_MINUTES),
              currentDate
            );
            const e = clampToDay(
              roundToSnap(finalEnd, SNAP_MINUTES),
              currentDate
            );

            updateEventLocal(id, s, e);

            reserve?.form?.setValue?.("time", s);
            reserve?.setStartTime?.(s);

            setTimeout(() => {
              const mins = Math.max(
                MIN_DURATION_MIN,
                differenceInMinutes(e, s)
              );
              reserve?.setDurationMins?.(mins);
              reserve?.form?.setValue?.("duration", { value: mins });
            }, 1);
          }}
        />
      ));
    });
  };

  return (
    <div className={cn("flex h-full flex-col", className)}>
      <ScrollArea
        ref={calendarRef}
        className={cn("flex-1 px-5 py-2 pl-0", scrollAreaProps?.className)}
        viewportClassName={cn("pt-4", scrollAreaProps?.viewportClassName)}
      >
        <div className="relative min-h-[400px]">
          {hourMarkers.map((hour, index) => (
            <HourMarker key={index} hour={hour} />
          ))}
          {isToday(currentDate) && (
            <CurrentTimeLine timeZone={office?.time_zone} />
          )}

          {/* NEW: timeline (left gutter) overlay using the same background drag handlers */}
          <div
            className="absolute top-0 bottom-0 left-0 w-16"
            onPointerDown={onBgPointerDown}
            onPointerMove={onBgPointerMove}
            onPointerUp={onBgPointerEnd}
            onPointerCancel={onBgPointerEnd}
            style={{ touchAction: "none" }}
          />

          <div
            className="absolute top-0 right-0 bottom-0 left-16"
            ref={eventsContainerRef}
            onPointerDown={onBgPointerDown}
            onPointerMove={onBgPointerMove}
            onPointerUp={onBgPointerEnd}
            onPointerCancel={onBgPointerEnd}
            style={{ touchAction: "none" }}
          >
            <AnimatePresence
              initial={false}
              mode="popLayout"
              custom={direction}
            >
              <motion.div
                key={`${currentDate.toISOString().split("T")[0]}-${reserve?.resource?.id || "no-resource"}`}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 }
                }}
                style={{ position: "absolute", width: "100%", height: "100%" }}
              >
                {renderEvents()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};
